import type { ReactNode } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Drag-to-reorder wrapper for a dashboard widget. Reads its
// sort-key from `id` and hands sortable-list membership + drag
// listeners to its child via the surrounding SortableContext.
//
// Activation is set at the sensor level in the parent DndContext:
//   * Desktop / mouse: PointerSensor with distance 8, so a plain
//     click doesn't trigger drag — the pointer has to move 8 px
//     first. Buttons / links inside the widget keep working.
//   * Touch: TouchSensor with a 1.5 s hold delay + a small
//     movement tolerance, so a tap fires normally, a swipe
//     scrolls the page normally, and only a stationary long-press
//     starts a drag.
//
// Touch-action management: the outer div stays `touch-auto` while
// idle so the browser scrolls the dashboard naturally on iOS. It
// only switches to `touch-none` once a drag is actually in flight
// — that's when we want the browser to stop competing with the
// drag gesture. A stray finger held on a widget never blocks
// scroll: the 1.5 s hold has to complete first.
export function SortableWidget({
  id, children,
}: {
  id: string;
  children: ReactNode;
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  } as const;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={
        "relative group "
        + (isDragging ? "touch-none cursor-grabbing" : "touch-auto cursor-grab")
      }
    >
      {/* Grab strip along the top edge — a visible affordance on
        * desktop hover. On mobile there's no hover state; the
        * actual drag activation is the 1.5 s long-press gesture. */}
      <div
        aria-hidden="true"
        className={
          "absolute inset-x-0 top-0 h-1.5 rounded-t-lg pointer-events-none transition-colors "
          + (isDragging
            ? "bg-brand-400/60"
            : "bg-slate-200/0 group-hover:bg-slate-300/70")
        }
      />
      {children}
    </div>
  );
}
